import { useEffect, useState } from "react";

import { Admin } from "./admin/Admin";
import { identifyUser, initAnalytics, resetAnalytics, track } from "./analytics";
import { Join } from "./auth/Join";
import { Login } from "./auth/Login";
import { Onboarding } from "./auth/Onboarding";
import { Chat } from "./chat/Chat";
import { AppShell, type Tab } from "./components/AppShell";
import { ClubSettingsScreen } from "./group/ClubSettingsScreen";
import { CreateClubScreen } from "./group/CreateClubScreen";
import { GroupsScreen } from "./group/GroupsScreen";
import { Home } from "./home/Home";
import { SmsOptIn } from "./sms/SmsOptIn";
import { NotificationsScreen } from "./settings/NotificationsScreen";
import { getMe, logout, stopImpersonating, type Me } from "./api";

/** /join/<CODE> — magic-link invite landing path. */
const JOIN_PATH = /^\/join\/([A-Z0-9-]+)\/?$/i;
/** /admin — secret admin panel, ADMIN_EMAILS-gated server-side. */
const ADMIN_PATH = /^\/admin\/?$/i;

// --- Minimal client-side routing (no router lib) ---------------------------
// URL scheme: / = Today, /groups[/<id>] = Groups, /groups/new = create a club,
// /groups/<id>/settings = club settings, /chat = Chat. The backend serves
// index.html for any non-API path, so these resolve on refresh + deep link.
type GroupView = "detail" | "new" | "settings";
type Route = { tab: Tab; groupId: string | null; view: GroupView };

function parsePath(pathname: string): Route {
  if (/^\/groups\/new\/?$/.test(pathname)) {
    return { tab: "groups", groupId: null, view: "new" };
  }
  const s = /^\/groups\/([^/]+)\/settings\/?$/.exec(pathname);
  if (s) return { tab: "groups", groupId: decodeURIComponent(s[1]!), view: "settings" };
  const g = /^\/groups(?:\/([^/]+))?\/?$/.exec(pathname);
  if (g) {
    return { tab: "groups", groupId: g[1] ? decodeURIComponent(g[1]) : null, view: "detail" };
  }
  if (/^\/chat\/?$/.test(pathname)) return { tab: "chat", groupId: null, view: "detail" };
  return { tab: "today", groupId: null, view: "detail" };
}

function pathForRoute(tab: Tab, groupId: string | null, view: GroupView): string {
  if (tab === "groups") {
    if (view === "new") return "/groups/new";
    if (view === "settings" && groupId) return `/groups/${groupId}/settings`;
    return groupId ? `/groups/${groupId}` : "/groups";
  }
  if (tab === "chat") return "/chat";
  return "/";
}

export default function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [joinCode, setJoinCode] = useState<string | null>(() => {
    const m = JOIN_PATH.exec(window.location.pathname);
    return m ? m[1]!.toUpperCase() : null;
  });
  const isAdminRoute = ADMIN_PATH.test(window.location.pathname);

  // Shell route (tab + selected group).
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));
  const [smsOpen, setSmsOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  // Getting-started checklist dismissal (lifted so the menu can re-open it).
  const [setupHidden, setSetupHidden] = useState(
    () => localStorage.getItem("clubbot_setup_hidden") === "1",
  );
  function dismissSetup() {
    localStorage.setItem("clubbot_setup_hidden", "1");
    setSetupHidden(true);
  }
  function reopenSetup() {
    localStorage.removeItem("clubbot_setup_hidden");
    setSetupHidden(false);
    navigate("today");
  }
  // Availability feature dismissal (✕ on the "Your week" card hides the card +
  // club grids; the "My availability" menu item brings them back).
  const [availHidden, setAvailHidden] = useState(
    () => localStorage.getItem("clubbot_avail_hidden") === "1",
  );
  function dismissAvail() {
    localStorage.setItem("clubbot_avail_hidden", "1");
    setAvailHidden(true);
  }
  function reopenAvail() {
    if (availHidden) track("availability_restored", {});
    localStorage.removeItem("clubbot_avail_hidden");
    setAvailHidden(false);
    navigate("today");
  }
  // When an in-screen "Ask" button hands off a question, jump to the Chat tab
  // and seed it. Chat clears this via `onSeedConsumed` once it's processed.
  const [pendingSeed, setPendingSeed] = useState<string | null>(null);

  useEffect(() => {
    initAnalytics();
    let cancelled = false;
    getMe()
      .then((m) => !cancelled && setMe(m))
      .catch(() => !cancelled && setMe(null));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (me) identifyUser(me.uid, me.email);
  }, [me]);

  // Sync route state with the back/forward buttons.
  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Deep link with a prefilled chat message: /chat?ask=... (e.g. the
  // "Set up a beacon" button in the reminder email). Seed Chat, land on it,
  // and strip the query so a refresh doesn't re-seed.
  useEffect(() => {
    const ask = new URLSearchParams(window.location.search).get("ask");
    if (ask) {
      setPendingSeed(ask);
      window.history.replaceState(null, "", "/chat");
      setRoute({ tab: "chat", groupId: null, view: "detail" });
    }
  }, []);

  function navigate(
    tab: Tab,
    groupId: string | null = null,
    opts: { replace?: boolean; view?: GroupView } = {},
  ) {
    const view = opts.view ?? "detail";
    const path = pathForRoute(tab, groupId, view);
    if (path !== window.location.pathname) {
      if (opts.replace) window.history.replaceState(null, "", path);
      else window.history.pushState(null, "", path);
    }
    setRoute({ tab, groupId, view });
  }

  if (me === undefined) {
    return (
      <div className="grid h-full place-items-center font-mono text-xs uppercase tracking-[0.22em] text-ink/55">
        Loading…
      </div>
    );
  }

  // Invite-link flow takes priority over everything else.
  if (joinCode) {
    return (
      <Join
        code={joinCode}
        me={me}
        onJoined={() => {
          setJoinCode(null);
          window.history.replaceState(null, "", "/");
          // After invite-link join: route to Chat tab so the user sees the
          // welcome message the backend already injected (api/groups.py
          // injects a profile-aware welcome that asks for their name when
          // the profile is incomplete, or surfaces next-steps when it's
          // complete). Without this, brand-new users land on an empty
          // Today tab and miss the onboarding prompt entirely.
          navigate("chat");
          getMe()
            .then((m) => setMe(m))
            .catch(() => setMe(null));
        }}
      />
    );
  }

  if (me === null) return <Login />;
  if (isAdminRoute) return <Admin me={me} />;

  // Onboarding gate: a signed-in user without a complete profile is trapped on
  // the bot-driven onboarding chat (no shell, no tabs) until it's done. The
  // join flow above runs first, so an invite is finished *then* they onboard.
  if (!me.onboarded) {
    return (
      <Onboarding
        me={me}
        onDone={(m) => {
          setMe(m);
          // Land on Today, same as the standard onboarding. The invite flow
          // routed here via the Chat tab (to show the injected welcome); without
          // this the user would be stranded on Chat after finishing setup.
          navigate("today");
        }}
        onSignOut={handleSignOut}
      />
    );
  }

  // Signed-in: render the Court-side shell. In-screen "Ask" buttons hand off a
  // prefilled question to the Chat tab; with no prefill they just open it.
  function handleAskAgent(prefill?: string) {
    if (prefill) setPendingSeed(prefill);
    navigate("chat");
  }

  async function handleExitImpersonation() {
    try {
      await stopImpersonating();
    } catch {
      // best-effort — fall through to the admin page either way.
    }
    window.location.assign("/admin");
  }

  async function handleSignOut() {
    resetAnalytics();
    try {
      await logout();
    } catch {
      // best-effort — clearing client state still signs the user out locally.
    }
    setMe(null);
  }

  return (
    <>
      <AppShell
        me={me}
        tab={route.tab}
        onTab={(t) => navigate(t)}
        onSignOut={handleSignOut}
        onTextUpdates={() => setSmsOpen(true)}
        onNotifications={() => setNotifOpen(true)}
        onShowSetup={reopenSetup}
        onShowAvailability={reopenAvail}
        onExitImpersonation={handleExitImpersonation}
      >
        {route.tab === "today" && (
          <Home
            me={me}
            onAskAgent={handleAskAgent}
            onOpenSms={() => setSmsOpen(true)}
            setupHidden={setupHidden}
            onDismissSetup={dismissSetup}
            availHidden={availHidden}
            onDismissAvail={dismissAvail}
            onReopenAvail={reopenAvail}
          />
        )}
        {route.tab === "groups" && route.view === "new" && (
          <CreateClubScreen
            onCreated={(id) => navigate("groups", id)}
            onCancel={() => navigate("groups", route.groupId)}
          />
        )}
        {route.tab === "groups" && route.view === "settings" && route.groupId && (
          <ClubSettingsScreen
            groupId={route.groupId}
            me={me}
            onBack={() => navigate("groups", route.groupId)}
          />
        )}
        {route.tab === "groups" && route.view === "detail" && (
          <GroupsScreen
            me={me}
            onAskAgent={handleAskAgent}
            activeGroupId={route.groupId}
            onSelectGroup={(id, replace) => navigate("groups", id, { replace })}
            onCreateClub={() => navigate("groups", null, { view: "new" })}
            onOpenSettings={(id) => navigate("groups", id, { view: "settings" })}
          />
        )}
        {route.tab === "chat" && (
          <Chat
            me={me}
            onSignOut={handleSignOut}
            pendingSeed={pendingSeed}
            onSeedConsumed={() => setPendingSeed(null)}
          />
        )}
      </AppShell>

      {smsOpen && <SmsOptIn onClose={() => setSmsOpen(false)} />}
      {notifOpen && (
        <NotificationsScreen
          onClose={() => setNotifOpen(false)}
          onManageTexts={() => {
            setNotifOpen(false);
            setSmsOpen(true);
          }}
        />
      )}
    </>
  );
}
